import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LeftArrow } from '../../../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../../../assets/icons/right-arrow.svg';
import { UpTimeInterface } from '../../../../interface/uptime-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchUpTimeListAndChartData } from '../../../../redux/slices/UpTimeListAndChartSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { changeDateFormat } from '../../../../shared-functions/DateTime';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import StationManagementApi from '../../../../utils/api/station-management';

import UptimeChart from './UptimeChart';

interface MaintenanceScheduleReportProperties {
  day: number;
  month: number;
  year: number;
  status: string;
}

interface TodayUptime {
  date: string;
  offlineDuration: number;
  onlineDuration: number;
}

/**
 * Split a segment into multiple days (UTC),
 * using a 24h cycle 00:00 → next day 00:00 (12 AM → 12 AM).
 * Each returned part has start and end on the same "logical day" row.
 * @param {any} seg The segment data
 * @returns {any[]} The split days
 */
const splitIntoDays = (seg: any) => {
  const start = new Date(seg.startsAt);
  const end = new Date(seg.endsAt);

  const parts: any[] = [];

  // Work entirely in UTC to avoid 06:30 / timezone shifts
  let cursor = new Date(start);

  while (cursor < end) {
    const year = cursor.getUTCFullYear();
    const month = cursor.getUTCMonth();
    const day = cursor.getUTCDate();

    // Day start and next day start in UTC (00:00)
    const dayStart = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
    const nextDayStart = new Date(Date.UTC(year, month, day + 1, 0, 0, 0, 0));

    // For this row, segment starts at:
    //  - real seg.start if this is the first day
    //  - 00:00 (dayStart) for middle days
    const sliceStart = cursor > dayStart ? cursor : dayStart;

    // Segment ends at:
    //  - real seg.end if it ends before next midnight
    //  - next midnight (nextDayStart) otherwise
    const sliceEnd = end < nextDayStart ? end : nextDayStart;

    if (sliceEnd > sliceStart) {
      parts.push({
        date: dayStart.toISOString().slice(0, 10), // "YYYY-MM-DD"
        startsAt: sliceStart.toISOString(),
        endsAt: sliceEnd.toISOString(),
        status: seg.status,
      });
    }

    // Move cursor to next day 00:00
    cursor = nextDayStart;
  }

  return parts;
};

/**
 * Uptime component
 * @returns {any} The rendered component
 */
function Uptime() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [allDates, setAllDates] = useState<any[]>([]);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [todayUptime, setTodayUpTime] = useState<TodayUptime>();
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [maintenanceReport, setMaintenanceReport] = useState<MaintenanceScheduleReportProperties[]>(
    [],
  );
  const { devices } = useAppSelector((s) => s.device);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const { upTimeListAndChart } = useAppSelector((state) => state.UpTime);
  const [uptimeChartList, setUptimeChartList] = useState<any[]>([]);

  const months = useMemo(
    () => [
      t('january'),
      t('february'),
      t('march'),
      t('april'),
      t('may'),
      t('june'),
      t('july'),
      t('august'),
      t('september'),
      t('october'),
      t('november'),
      t('december'),
    ],
    [t],
  );

  const column = useMemo(
    () => [
      {
        label: t('date'),
        accessorKey: 'date' as keyof UpTimeInterface,
      },
      {
        label: t('downTimeDuration'),
        accessorKey: 'offlineDuration' as keyof UpTimeInterface,
        /**
         * Custom down time data
         * @param {UpTimeInterface} currenRow The current row data
         * @returns {any} The formatted duration
         */
        customData: (currenRow: UpTimeInterface) => (
          <p>
            {ToFixedValue(currenRow.offlineDuration)} {t('hrs')}
          </p>
        ),
      },
      {
        label: t('uptimeHour'),
        accessorKey: 'onlineDuration' as keyof UpTimeInterface,
        /**
         * Custom up time data
         * @param {UpTimeInterface} currenRow The current row data
         * @returns {any} The formatted duration
         */
        customData: (currenRow: UpTimeInterface) => (
          <p>
            {ToFixedValue(currenRow.onlineDuration)} {t('hrs')}
          </p>
        ),
      },
    ],
    [t],
  );

  /**
   * Get the all days use month function
   * @param {number} month The month index
   * @param {number} year The year
   */
  const getAllDays: (month: number, year: number) => void = (month: number, year: number) => {
    const date = new Date(year, month, 1);
    let days = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    days = days.map((valueChange) => ({
      date: changeDateFormat(valueChange.toString()),
      number: valueChange.getDate(),
      day: valueChange.getDay(),
    }));
    if (days[0].day > 0) {
      const firstMonth = days[0].day;
      for (let index = 0; index < firstMonth; index += 1) {
        days = [{}, ...days];
      }
    }
    setAllDates(days);
  };

  /**
   * Handle the month change function
   * @param {string} type The change type ('add' or 'sub')
   */
  const changeMonth = (type: string) => {
    let changeValue = 0;
    let changeYear = currentYear;
    if (type === 'add') {
      changeValue = currentMonth + 1;
      if (changeValue === months.length) {
        changeValue = 0;
        changeYear = currentYear + 1;
        setCurrentYear(currentYear + 1);
      }
      getAllDays(changeValue, changeYear);

      setCurrentMonth(changeValue);
    } else {
      changeValue = currentMonth - 1;
      if (changeValue === -1) {
        changeValue = 11;
        changeYear = currentYear - 1;
        setCurrentYear(currentYear - 1);
      }
      getAllDays(changeValue, changeYear);
      setCurrentMonth(changeValue);
    }
  };

  /**
   * Function to return a maintenance
   * @param {string} date The date string
   * @returns {string} The maintenance status
   */
  const handleShowMaintenanceSchedule = (date: string): string => {
    const [year, month, day] = date.split('-');

    const numericDay = Number(day); // 1
    const numericMonth = Number(month); // 10

    const maintenance = maintenanceReport.find(
      (eachReport) =>
        eachReport?.day?.toString() === numericDay.toString() &&
        eachReport?.month?.toString() === numericMonth.toString() &&
        eachReport?.year?.toString() === year,
    );

    if (!maintenance) return 'no-schedule';

    return maintenance?.status;
  };

  /**
   * Function to fetch maintenance schedule report
   * @param {any} data The data to fetch
   */
  const handleFetchMaintenanceReport = async (data: any) => {
    try {
      const response = await StationManagementApi.fetchUptimeListAndChart(data);
      setMaintenanceReport(response.data);
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        },
      });
      throw error;
    }
  };

  /**
   * Returns "online-high", "offline-high", or "" for each date
   * @param {string} dateString The date string
   * @returns {string} The highlight class
   */
  const getDayStatusHighlight = (dateString: string) => {
    if (!dateString) return '';

    // format of dateString: "2025-12-01"
    const matchingRecord = upTimeListAndChart.find((item) => item.date === dateString);

    if (!matchingRecord) return '';

    const { onlineDuration, offlineDuration } = matchingRecord;

    if (onlineDuration > offlineDuration) return 'online-high';
    if (offlineDuration > onlineDuration) return 'offline-high';

    return '';
  };

  /**
   * Fetching today's uptime
   */
  const fetchTodayUptime = async () => {
    try {
      const response = await StationManagementApi.fetUpTimeTodayData({
        chargingStation: selectedChargingPoint?.id,
      });
      setTodayUpTime(response?.data[0]);
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        },
      });
    }

    return null;
  };

  /**
   * Helper function for fetching uptime data
   */
  const fetchUptimeListData = () => {
    const formattedMonth = String(currentMonth + 1).padStart(2, '0');
    dispatch(
      fetchUpTimeListAndChartData({
        chargingStation: selectedChargingPoint?.id,
        month: formattedMonth,
        year: String(currentYear),
      }),
    );
  };

  /**
   * function refreshing table data
   */
  const handleRefresh = () => {
    fetchUptimeListData();
  };

  useEffect(() => {
    getAllDays(currentMonth, currentYear);
  }, []);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;
    fetchTodayUptime();
  }, [selectedChargingPoint?.id]);

  useEffect(() => {
    handleFetchMaintenanceReport({
      chargingStation: selectedChargingPoint?.id,
      month: '11',
      year: '2025',
    });
  }, [devices]);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;
    fetchUptimeListData();
  }, [currentMonth, currentYear, selectedChargingPoint?.id]);

  useEffect(() => {
    if (!upTimeListAndChart || upTimeListAndChart.length === 0) return;
    const response = upTimeListAndChart;

    /** grouped by date */
    const dailyMap: Record<string, any[]> = {};

    response.forEach((item: any) => {
      item.times.forEach((timeItem: any) => {
        const parts = splitIntoDays(timeItem);

        parts.forEach((p: any) => {
          if (!dailyMap[p.date]) dailyMap[p.date] = [];
          dailyMap[p.date].push({
            startsAt: p.startsAt,
            endsAt: p.endsAt,
            status: p.status,
          });
        });
      });
    });
    /** Convert map → array sorted by date */
    const finalResult = Object.keys(dailyMap)
      .sort()
      .map((date) => ({
        date,
        times: dailyMap[date].sort(
          (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
        ),
      }));

    setUptimeChartList(finalResult);
  }, [upTimeListAndChart]);

  return (
    <div className="uptime-wrap" dir={i18n.dir()}>
      <div className="uptime-container">
        <div className="uptime-top">
          <div className="left-content">
            <div className="upcoming-maintenance-calendar-wrap">
              <div className="calender-header">
                <div
                  className="calender-header-left"
                  onClick={() => changeMonth('sub')}
                  role="presentation"
                  style={{
                    transform: i18n.dir() === 'rtl' ? 'rotate(0deg)' : 'rotate(180deg)',
                  }}>
                  <LeftArrow />
                </div>
                <div className="calender-header-center">
                  {months[currentMonth]} - {currentYear}
                </div>
                <div
                  className="calender-header-right"
                  onClick={() => changeMonth('add')}
                  role="presentation"
                  style={{
                    transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}>
                  <RightArrow />
                </div>
              </div>
              <div className="calender-body">
                <div className="calender-header-items">
                  <div className="header-item">{t('sun')}</div>
                  <div className="header-item">{t('mon')}</div>
                  <div className="header-item">{t('tue')}</div>
                  <div className="header-item">{t('wed')}</div>
                  <div className="header-item">{t('thu')}</div>
                  <div className="header-item">{t('fri')}</div>
                  <div className="header-item">{t('sat')}</div>
                </div>
                <div className="calender-body-items">
                  {allDates.map((dateDetail: any) =>
                    Object.keys(dateDetail).length === 0 ? (
                      <div className="header-item" />
                    ) : (
                      <div
                        className="header-item"
                        // onClick={() => handleValueChange(dateDetail.date)}
                        role="presentation">
                        <p
                          className={
                            getDayStatusHighlight(dateDetail?.date) ||
                            handleShowMaintenanceSchedule(dateDetail?.date || '')
                          }>
                          {dateDetail.number}
                        </p>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="right-content">
            <UptimeChart data={uptimeChartList} todayUptime={todayUptime?.onlineDuration} />
          </div>
        </div>
        <div className="uptime-bottom">
          <CustomTable
            serialNo
            header={column}
            data={upTimeListAndChart}
            handleRefresh={handleRefresh}
          />
        </div>
      </div>
    </div>
  );
}

export default Uptime;
